import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiService } from './api.service';
import {
  ChecklistItem,
  ChecklistSearchRequest,
  ChecklistUpdateRequest,
  VaultDocument,
  UploadDetail,
  UploadSearchRequest,
  DocumentVerifyRequest,
  DocumentRejectRequest,
  TemplateApiResponse
} from '../types/Contract';

/**
 * Checklist request payload for creating company document checklist
 */
export interface ChecklistCreateRequest {
  b2bContactId?: string;
  demanderBusinessId: string;
  uploaderBusinessId?: string;
  comDocumentId: string;
  documentName: string;
  documentCategory: string;
  description?: string;
  isMandatory: boolean;
  hasExpiry?: boolean;
  defaultExpiryMonths?: number;
  expiryReminderDays?: number[];
  allowedFileTypes?: string[];
  maxFileSizeMB?: number;
  displayOrder?: number;
  fieldSchema?: any[];
  createdBy?: string;
}

/**
 * Service for Company Document Checklist APIs (APIs 13-26)
 * Base URL: /api/contract/company-document-checklist
 *
 * Manages: Checklists (13-17), Vault (18-20), Upload/Verify/Reject (21-26)
 */
@Injectable({
  providedIn: 'root'
})
export class CompanyDocumentChecklistService {

  private readonly BASE_PATH = 'api/contract/company-document-checklist';

  constructor(private apiService: ApiService) {}

  // ============================================================================
  // CHECKLIST APIs (13-17)
  // ============================================================================

  /**
   * API 13: Create a new company document checklist item
   * POST /api/contract/company-document-checklist/create
   */
  createChecklist(request: ChecklistCreateRequest): Observable<TemplateApiResponse<any>> {
    return this.apiService.create(`${this.BASE_PATH}/create`, request);
  }

  /**
   * API 14: Update company document checklist (mandatory/status)
   * PUT /api/contract/company-document-checklist/update
   */
  updateChecklist(request: ChecklistUpdateRequest): Observable<TemplateApiResponse<any>> {
    return this.apiService.updatePut(`${this.BASE_PATH}/update`, request);
  }

  /**
   * API 15: Get company document checklist by ID
   * GET /api/contract/company-document-checklist/{checklistId}
   */
  getChecklistById(checklistId: string): Observable<TemplateApiResponse<{ checklist: ChecklistItem; upload: UploadDetail }>> {
    return this.apiService.query(`${this.BASE_PATH}/${checklistId}`);
  }

  /**
   * API 16: Search company document checklists
   * POST /api/contract/company-document-checklist/search
   */
  searchChecklists(request: ChecklistSearchRequest): Observable<TemplateApiResponse<{ checklists: ChecklistItem[] }>> {
    return this.apiService.create(`${this.BASE_PATH}/search`, request);
  }

  /**
   * API 17: Delete company document checklist (soft delete → ARCHIVED)
   * DELETE /api/contract/company-document-checklist/{checklistId}
   */
  deleteChecklist(checklistId: string): Observable<TemplateApiResponse<{}>> {
    return this.apiService.delete(`${this.BASE_PATH}/${checklistId}`);
  }

  /**
   * Update vault document status (archive/activate)
   * PUT /api/contract/company-document-checklist/vault/update
   */
  updateVaultDocument(vaultDocumentId: string, status: 'ACTIVE' | 'ARCHIVED'): Observable<TemplateApiResponse<any>> {
    return this.apiService.updatePut(`${this.BASE_PATH}/vault/update`, { vaultDocumentId, status });
  }

  /**
   * Delete vault document permanently
   * DELETE /api/contract/company-document-checklist/vault/{vaultDocumentId}
   */
  deleteVaultDocument(vaultDocumentId: string): Observable<TemplateApiResponse<any>> {
    return this.apiService.delete(`${this.BASE_PATH}/vault/${vaultDocumentId}`);
  }

  // ============================================================================
  // VAULT APIs (18-20)
  // ============================================================================

  /**
   * API 18: Check vault for existing company document
   * GET /api/contract/company-document-checklist/vault/check/{businessId}/{comDocumentId}
   */
  checkVault(businessId: string, comDocumentId: string): Observable<TemplateApiResponse<{ vaultDocuments: VaultDocument[]; exists: boolean }>> {
    return this.apiService.query(`${this.BASE_PATH}/vault/check/${businessId}/${comDocumentId}`);
  }

  /**
   * API 19: Search vault documents for a business
   * POST /api/contract/company-document-checklist/vault/{businessId}
   */
  getVaultDocuments(businessId: string, searchContent?: string, limit?: number, page?: number, documentCategory?: string): Observable<TemplateApiResponse<{ vaultDocuments: VaultDocument[] }>> {
    const payload: any = {};
    if (searchContent) {
      payload.searchContent = searchContent;
    }
    if (limit) {
      payload.limit = limit;
    }
    if (page) {
      payload.page = page;
    }
    if (documentCategory) {
      payload.documentCategory = documentCategory;
    }
    return this.apiService.create(`${this.BASE_PATH}/vault/${businessId}`, payload);
  }

  /**
   * API 20: Map document from vault (reuse existing)
   * POST /api/contract/company-document-checklist/vault/map?checklistId=X&vaultDocumentId=Y&uploadedBy=Z
   */
  mapVaultDocument(checklistId: string, vaultDocumentId: string, uploadedBy: string, b2bContactId: string): Observable<TemplateApiResponse<any>> {
    const params = `checklistId=${checklistId}&vaultDocumentId=${vaultDocumentId}&uploadedBy=${uploadedBy}&b2bContactId=${b2bContactId}`;
    return this.apiService.create(`${this.BASE_PATH}/vault/map?${params}`, {});
  }

  // ============================================================================
  // VAULT UPLOAD API
  // ============================================================================

  /**
   * Vault Upload: Upload document directly to company vault (My Company context)
   * POST /api/contract/company-document-checklist/vault/upload?businessId=X&createdBy=Y
   * Body: multipart — file (@RequestPart), vaultRequest (@RequestPart, JSON), fieldValues (@RequestPart, JSON, optional)
   */
  vaultUpload(file: File, businessId: string, createdBy: string, vaultRequest: {
    comDocumentId: string;
    documentName: string;
    documentCategory: string;
    description?: string;
    hasExpiry: boolean;
    defaultExpiryMonths?: number;
    expiryReminderDays?: number[];
    expiryDate?: string;
    displayOrder?: number;
  }, fieldValues?: any[]): Observable<TemplateApiResponse<any>> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('vaultRequest', new Blob([JSON.stringify(vaultRequest)], { type: 'application/json' }));
    if (fieldValues) {
      formData.append('fieldValues', new Blob([JSON.stringify(fieldValues)], { type: 'application/json' }));
    }
    const params = `businessId=${encodeURIComponent(businessId)}&createdBy=${encodeURIComponent(createdBy)}`;
    return this.apiService.create(`${this.BASE_PATH}/vault/upload?${params}`, formData);
  }

  // ============================================================================
  // UPLOAD APIs (21-26)
  // ============================================================================

  /**
   * API 21: Upload company document
   * POST /api/contract/company-document-checklist/upload?checklistId=X&uploadedBy=Y&b2bContactId=Z
   * Body: multipart — file (@RequestPart), fieldValues (@RequestPart, JSON)
   */
  uploadDocument(checklistId: string, file: File, uploadedBy: string, b2bContactId: string, fieldValues?: any[]): Observable<TemplateApiResponse<any>> {
    const formData = new FormData();
    formData.append('file', file);
    if (fieldValues && fieldValues.length > 0) {
      formData.append('fieldValues', new Blob([JSON.stringify(fieldValues)], { type: "application/json" }));
    }
    const params = `checklistId=${checklistId}&uploadedBy=${uploadedBy}&b2bContactId=${encodeURIComponent(b2bContactId)}`;
    return this.apiService.create(`${this.BASE_PATH}/upload?${params}`, formData);
  }

  /**
   * API 22: Verify company document
   * PUT /api/contract/company-document-checklist/verify?uploadId=X&verifiedBy=Y
   */
  verifyDocument(uploadId: string, verifiedBy: string): Observable<TemplateApiResponse<any>> {
    const params = `uploadId=${uploadId}&verifiedBy=${verifiedBy}`;
    return this.apiService.updatePut(`${this.BASE_PATH}/verify?${params}`, {});
  }

  /**
   * API 23: Reject company document
   * PUT /api/contract/company-document-checklist/reject?uploadId=X&verifiedBy=Y&rejectionReason=Z
   */
  rejectDocument(uploadId: string, verifiedBy: string, rejectionReason: string): Observable<TemplateApiResponse<any>> {
    const params = `uploadId=${uploadId}&verifiedBy=${encodeURIComponent(verifiedBy)}&rejectionReason=${encodeURIComponent(rejectionReason)}`;
    return this.apiService.updatePut(`${this.BASE_PATH}/reject?${params}`, {});
  }

  /**
   * API 24: Get upload detail by ID (with version history)
   * GET /api/contract/company-document-checklist/upload/{uploadId}
   */
  getUploadDetail(uploadId: string): Observable<TemplateApiResponse<{ upload: UploadDetail }>> {
    return this.apiService.query(`${this.BASE_PATH}/upload/${uploadId}`);
  }

  /**
   * API 25: Search company document uploads
   * POST /api/contract/company-document-checklist/upload/search
   */
  searchUploads(request: UploadSearchRequest): Observable<TemplateApiResponse<{ uploads: UploadDetail[] }>> {
    return this.apiService.create(`${this.BASE_PATH}/upload/search`, request);
  }

  /**
   * Delete a specific version of an uploaded document
   * DELETE /api/contract/company-document-checklist/upload/version
   */
  deleteUploadVersion(uploadId: string, versionNumber: number): Observable<TemplateApiResponse<any>> {
    return this.apiService.deleteWithPayload(`${this.BASE_PATH}/upload/version`, { uploadId, versionNumber });
  }

  /**
   * API 26: Mark company document as expired
   * PUT /api/contract/company-document-checklist/upload/mark-expired/{uploadId}
   */
  markExpired(uploadId: string): Observable<TemplateApiResponse<any>> {
    return this.apiService.updatePut(`${this.BASE_PATH}/upload/mark-expired/${uploadId}`, {});
  }

  // ============================================================================
  // FILE DOWNLOAD
  // ============================================================================

  /**
   * Download company document file as ArrayBuffer (for PDF viewer / download)
   * GET /contract/download?fileUrl=...
   */
  downloadFile(fileUrl: string): Observable<ArrayBuffer> {
    return this.apiService.queryBlob(`contract/download?fileUrl=${encodeURIComponent(fileUrl)}`);
  }

  // ============================================================================
  // HELPER METHODS
  // ============================================================================

  isSuccess(response: TemplateApiResponse<any>): boolean {
    return response.code === '00000';
  }

  isNoData(response: TemplateApiResponse<any>): boolean {
    return response.code === '22222';
  }

  isError(response: TemplateApiResponse<any>): boolean {
    return response.code === '11111';
  }
}
